import { useRef, useState, useEffect } from 'react';

// Envoltorio para tablas anchas.
// Mantiene el scroll horizontal, pero deja claro que hay más contenido al lado:
//   - Muestra una sombra degradada en el borde donde queda contenido oculto.
//   - En móvil muestra un aviso "Desliza para ver más" la primera vez.
//
// Uso:
//   <TablaScroll>
//     <table>...</table>
//   </TablaScroll>

export default function TablaScroll({ children, className = '' }) {
  const ref = useRef(null);
  const [puedeIzquierda, setPuedeIzquierda] = useState(false);
  const [puedeDerecha, setPuedeDerecha] = useState(false);

  const revisarScroll = () => {
    const el = ref.current;
    if (!el) return;
    const margen = 4; // tolerancia para evitar parpadeos por redondeo
    setPuedeIzquierda(el.scrollLeft > margen);
    setPuedeDerecha(el.scrollLeft + el.clientWidth < el.scrollWidth - margen);
  };

  useEffect(() => {
    revisarScroll();
    const el = ref.current;
    if (!el) return;

    el.addEventListener('scroll', revisarScroll, { passive: true });
    window.addEventListener('resize', revisarScroll);

    // Si el contenido cambia de tamaño (por ejemplo al filtrar), recalcular
    const observer = new ResizeObserver(revisarScroll);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', revisarScroll);
      window.removeEventListener('resize', revisarScroll);
      observer.disconnect();
    };
  }, [children]);

  return (
    <div className="relative">
      {/* Aviso de deslizar (solo móvil y solo si hay contenido oculto) */}
      {puedeDerecha && (
        <div className="md:hidden absolute -top-7 right-0 z-10 flex items-center gap-1 text-xs text-gray-500">
          <span>Desliza para ver más</span>
          <svg className="w-3.5 h-3.5 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </div>
      )}

      {/* Sombra izquierda */}
      {puedeIzquierda && (
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r from-gray-200/80 to-transparent" />
      )}

      {/* Sombra derecha */}
      {puedeDerecha && (
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l from-gray-200/80 to-transparent" />
      )}

      <div ref={ref} className={`overflow-x-auto ${className}`}>
        {children}
      </div>
    </div>
  );
}
